import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import Add from "./Add";
import Edit from "./Edit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";

import { formatJalaliDateTime } from "../utils/dateFormat";
const Index = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(null);
  const [openEdit, setOpenEdit] = useState(null);
  const [editData, setEditData] = useState(null);

  // The fetch hook will re-run whenever searchTerm, page, or rowsPerPage changes
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["category", searchTerm, page, rowsPerPage],
    `shop/categories/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/categories", token);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  // Determine categories list and total count
  const categories = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : categories.length;

  if (isError) {
    return <ErrorPage message={t("shop.failedToLoadCategories")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section with Title and "Add New Category" Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("category.categoryList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setOpenAdd(true);
          }}
        >
          {t("category.addNewCategory")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("category.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("category.searchCategories")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table with integrated loading skeleton */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="category table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("category.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("category.name")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("category.description")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("category.category")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("category.createdAt")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("category.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Name",
                      "Description",
                      "Branch",
                      "Created At",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + categories.indexOf(category) + 1}</TableCell>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>{category.description}</TableCell>
                    <TableCell>{category.shop_details?.name}</TableCell>
                    <TableCell>
                      {category.created_at
                        ? formatJalaliDateTime(category.created_at)
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(category);
                          setOpenEdit(true);
                        }}
                      >
                        {t("category.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          handleDelete(category?.id);
                        }}
                      >
                        {t("category.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("category.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {openAdd && (
        <Add
          open={openAdd}
          onClose={() => {
            setOpenAdd(false);
          }}
          token={token}
        />
      )}

      {openEdit && (
        <Edit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
          }}
          editData={editData}
          token={token}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default Index;
